// Freshdesk #677: helpers for the claim drawer's "Generate DSAR document"
// action. No React here, so the filename, the download and the error wording
// can be tested on their own.

export const DSAR_GENERIC_ERROR = "Couldn't generate the DSAR letter. Please try again.";
export const DSAR_NETWORK_ERROR =
  "Couldn't reach the server to generate the DSAR letter. Please try again.";

/** The name the backend gives the letter. Used when the browser can't read it. */
export const dsarFallbackFilename = (claimId: string): string =>
  `DSAR response - ${claimId}.docx`;

/**
 * The filename from a Content-Disposition header, or null when there is none.
 * Backend CORS does not expose this header, so cross-origin it is usually
 * missing and the caller falls back to dsarFallbackFilename.
 */
export function filenameFromContentDisposition(header: unknown): string | null {
  if (typeof header !== 'string' || !header) return null;
  const encoded = header.match(/filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/);
  if (encoded) {
    try {
      const name = decodeURIComponent(encoded[1].trim());
      if (name) return name;
    } catch {
      // Malformed encoding: try the plain filename below.
    }
  }
  const quoted = header.match(/filename\s*=\s*"([^"]+)"/);
  if (quoted) return quoted[1];
  const bare = header.match(/filename\s*=\s*([^;]+)/);
  const name = bare ? bare[1].trim() : '';
  return name || null;
}

/** Save a Blob to the user's Downloads via a hidden <a download> link. */
export function saveBlobAsFile(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  try {
    link.click();
  } finally {
    link.remove();
    URL.revokeObjectURL(url);
  }
}

// jsdom's Blob has no .text(), so fall back to FileReader there.
function readBlobText(blob: Blob): Promise<string> {
  if (typeof (blob as { text?: unknown }).text === 'function') {
    return blob.text();
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

function errorFromBody(body: unknown): string | null {
  if (typeof body === 'string') {
    try {
      return errorFromBody(JSON.parse(body));
    } catch {
      return null;
    }
  }
  if (body && typeof body === 'object') {
    const message = (body as { error?: unknown }).error;
    if (typeof message === 'string' && message.trim()) return message.trim();
  }
  return null;
}

/**
 * A plain message for a failed DSAR request. The request uses
 * responseType 'blob', so the backend's JSON `{ error }` arrives as a Blob
 * and has to be read before the message can be shown.
 */
export async function dsarErrorMessage(err: unknown): Promise<string> {
  const ax = err as {
    response?: { data?: unknown };
    request?: unknown;
    isAxiosError?: boolean;
  } | null;
  const response = ax?.response;
  if (!response) {
    // Axios sets `request` but no `response` when the server never answered.
    return ax && (ax.request || ax.isAxiosError) ? DSAR_NETWORK_ERROR : DSAR_GENERIC_ERROR;
  }
  let body = response.data;
  if (typeof Blob !== 'undefined' && body instanceof Blob) {
    try {
      body = await readBlobText(body);
    } catch {
      return DSAR_GENERIC_ERROR;
    }
  }
  // Anything that is not the backend's JSON (e.g. a gateway HTML page) gets
  // the plain fallback rather than raw markup.
  return errorFromBody(body) || DSAR_GENERIC_ERROR;
}
